import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import { EditorSlideOver, PageSectionCard } from "../../../components/layout";
import { AppButton } from "../../../components/form";
import DataTableWithPagination from "../../../components/DataTable";
import { dashboardQueryService } from "../../../services/dashboardQueryService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";
import QueryEditPage from "./QueryEditPage";

type EditorTarget = { mode: "create" } | { mode: "edit"; id: string };

export default function QueryListPage() {
  const { t } = useTranslation();
  const tableRef = useRef<{ refresh: () => void } | null>(null);
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const packageLimits = usePackageLimits();
  const [editor, setEditor] = useState<EditorTarget | null>(null);

  const refreshTable = useCallback(() => {
    tableRef.current?.refresh();
  }, []);

  const closeEditor = useCallback(() => setEditor(null), []);

  const handleEdit = useCallback((row: { id?: string }) => {
    if (row.id) setEditor({ mode: "edit", id: row.id });
  }, []);

  const handleDelete = useCallback(
    async (row: { id?: string; name?: string }) => {
      if (!row.id) return;
      try {
        await confirmDelete({
          itemName: row.name || row.id,
          titleKey: "common.delete",
          descriptionKey: "query.deleteConfirm",
        });
      } catch (error) {
        if (isCancelled(error)) return;
        throw error;
      }
      try {
        await dashboardQueryService.remove(row.id);
        refreshTable();
      } catch (error) {
        window.dispatchEvent(
          new CustomEvent("showGlobalNotification", {
            detail: {
              color: "warning",
              icon: "warning",
              title: t("common.delete"),
              content: t("query.inUse"),
            },
          })
        );
        console.error(error);
      }
    },
    [confirmDelete, isCancelled, refreshTable, t]
  );

  const cols = useMemo(
    () => [
      {
        name: t("query.name"),
        selector: (row: { name?: string }) => row.name || "",
        sortable: true,
      },
      {
        name: t("query.description"),
        selector: (row: { description?: string }) => row.description || "",
        sortable: false,
      },
    ],
    [t]
  );

  return (
    <PageListContainer>
      <PageHeader
        title={t("query.title")}
        actions={
          <AppButton
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setEditor({ mode: "create" })}
          >
            {t("query.create")}
          </AppButton>
        }
      />
      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxDashboardQueries != null}>
          {t("packageLimits.queries", {
            count: formatLimitValue(packageLimits.maxDashboardQueries, t("packageLimits.unlimited")),
          })}
        </PackageLimitHint>
      )}
      <PageSectionCard title={t("query.listTitle")} noPadding>
        <DataTableWithPagination
          ref={tableRef}
          cols={cols}
          fetchUrl="/dashboard-queries"
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          searchKey="name"
          searchCollapseToIcon
        />
      </PageSectionCard>

      <EditorSlideOver
        open={Boolean(editor)}
        onClose={closeEditor}
        title={editor?.mode === "create" ? t("query.create") : t("query.edit")}
      >
        {editor?.mode === "create" ? (
          <QueryEditPage mode="create" embedded onClose={closeEditor} onSaved={refreshTable} />
        ) : editor?.mode === "edit" ? (
          <QueryEditPage
            mode="edit"
            embedded
            queryId={editor.id}
            onClose={closeEditor}
            onSaved={refreshTable}
          />
        ) : null}
      </EditorSlideOver>
    </PageListContainer>
  );
}
